import {
  useEffect,
  useRef,
  useState
} from 'react';

import styles from './VehiclePhotoEditor.module.css';

function VehiclePhotoEditor({
  open = false,
  imageFile = null,
  onCancel,
  onConfirm
}) {
  const [imageUrl, setImageUrl] = useState('');
  const [scale, setScale] = useState(1);

  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });

  const [processing, setProcessing] =
    useState(false);

  /*
    Referencias reales del DOM.

    Las utilizamos para calcular exactamente
    qué parte de la fotografía está dentro
    del marco que ve el usuario.
  */
  const imageRef = useRef(null);
  const frameRef = useRef(null);

  const dragRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0
  });

  /*
    Convierte temporalmente el archivo
    seleccionado en una URL local.
  */
  useEffect(() => {
    if (!imageFile) {
      setImageUrl('');
      return;
    }

    const url =
      URL.createObjectURL(imageFile);

    setImageUrl(url);

    setScale(1);

    setPosition({
      x: 0,
      y: 0
    });

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [imageFile]);

  function handlePointerDown(event) {
    if (!imageUrl) return;

    dragRef.current = {
      active: true,

      startX: event.clientX,
      startY: event.clientY,

      originX: position.x,
      originY: position.y
    };

    event.currentTarget.setPointerCapture?.(
      event.pointerId
    );
  }

  function handlePointerMove(event) {
    if (!dragRef.current.active) return;

    const deltaX =
      event.clientX -
      dragRef.current.startX;

    const deltaY =
      event.clientY -
      dragRef.current.startY;

    setPosition({
      x:
        dragRef.current.originX +
        deltaX,

      y:
        dragRef.current.originY +
        deltaY
    });
  }

  function handlePointerUp(event) {
    dragRef.current.active = false;

    event.currentTarget.releasePointerCapture?.(
      event.pointerId
    );
  }

  function handleZoom(event) {
    setScale(
      Number(event.target.value)
    );
  }

  function handleReset() {
    setScale(1);

    setPosition({
      x: 0,
      y: 0
    });
  }

  /*
    Genera físicamente una nueva imagen
    utilizando exactamente el encuadre
    visible dentro del marco.
  */
async function generarImagenFinal() {
  const imageElement =
    imageRef.current;

  const frameElement =
    frameRef.current;

  if (
    !imageElement ||
    !frameElement
  ) {
    throw new Error(
      'No fue posible obtener el área de edición'
    );
  }

  const naturalWidth =
    imageElement.naturalWidth;

  const naturalHeight =
    imageElement.naturalHeight;

  if (
    !naturalWidth ||
    !naturalHeight
  ) {
    throw new Error(
      'La fotografía todavía no está disponible'
    );
  }

  /*
    Tamaño REAL del área visible del editor.
  */
  const frameRect =
    frameElement.getBoundingClientRect();

  const frameWidth =
    frameRect.width;

  const frameHeight =
    frameRect.height;

  if (
    frameWidth <= 0 ||
    frameHeight <= 0
  ) {
    throw new Error(
      'El tamaño del editor no es válido'
    );
  }

  /*
    IMPORTANTE:

    El CSS utiliza:

      width: 100%;
      height: 100%;
      object-fit: contain;

    Por eso calculamos aquí cuánto ocupa
    REALMENTE la fotografía dentro del marco
    antes de aplicar zoom.
  */
  const containScale =
    Math.min(
      frameWidth /
        naturalWidth,

      frameHeight /
        naturalHeight
    );

  /*
    Tamaño real de la fotografía
    a escala 1.
  */
  const baseImageWidth =
    naturalWidth *
    containScale;

  const baseImageHeight =
    naturalHeight *
    containScale;

  /*
    Aplicamos exactamente el zoom
    que escogió el usuario.
  */
  const renderedWidth =
    baseImageWidth *
    scale;

  const renderedHeight =
    baseImageHeight *
    scale;

  /*
    El centro de la fotografía parte
    del centro del recuadro.

    position.x / position.y corresponden
    exactamente al movimiento realizado
    con el dedo.
  */
  const renderedLeft =
    frameWidth / 2 +
    position.x -
    renderedWidth / 2;

  const renderedTop =
    frameHeight / 2 +
    position.y -
    renderedHeight / 2;

  /*
    Resolución definitiva almacenada.

    Mantiene exactamente la proporción
    296 × 192 del carrusel/editor.
  */
  const outputWidth = 1200;

  const outputHeight =
    Math.round(
      outputWidth *
      (
        frameHeight /
        frameWidth
      )
    );

  const canvas =
    document.createElement(
      'canvas'
    );

  canvas.width =
    outputWidth;

  canvas.height =
    outputHeight;

  const context =
    canvas.getContext('2d');

  if (!context) {
    throw new Error(
      'No fue posible procesar la fotografía'
    );
  }

  /*
    Mismo fondo que utilizamos
    en el editor.

    Esto solamente será visible si
    el usuario aleja tanto la fotografía
    que deja algún espacio vacío.
  */
  context.fillStyle =
    '#050b14';

  context.fillRect(
    0,
    0,
    outputWidth,
    outputHeight
  );

  /*
    Conversión de coordenadas del editor
    a coordenadas del archivo final.
  */
  const outputScaleX =
    outputWidth /
    frameWidth;

  const outputScaleY =
    outputHeight /
    frameHeight;

  const destinationX =
    renderedLeft *
    outputScaleX;

  const destinationY =
    renderedTop *
    outputScaleY;

  const destinationWidth =
    renderedWidth *
    outputScaleX;

  const destinationHeight =
    renderedHeight *
    outputScaleY;

  /*
    DIBUJAMOS LA FOTO ORIGINAL COMPLETA
    en la misma posición y tamaño que
    tiene visualmente en el editor.

    Todo lo que queda fuera del canvas
    se recorta automáticamente.

    Este es el crop real.
  */
  context.drawImage(
    imageElement,

    0,
    0,
    naturalWidth,
    naturalHeight,

    destinationX,
    destinationY,
    destinationWidth,
    destinationHeight
  );

  /*
    Generamos WebP optimizado.

    Ya no guardamos la fotografía original.
  */
  const blob =
    await new Promise(
      (resolve, reject) => {
        canvas.toBlob(
          (result) => {
            if (result) {
              resolve(result);
            } else {
              reject(
                new Error(
                  'No fue posible generar la fotografía final'
                )
              );
            }
          },

          'image/webp',
          0.78
        );
      }
    );

  const originalName =
    imageFile.name ||
    'vehicle';

  const cleanName =
    originalName.replace(
      /\.[^/.]+$/,
      ''
    );

  return new File(
    [blob],

    `${cleanName}.webp`,

    {
      type: 'image/webp',
      lastModified:
        Date.now()
    }
  );
}

  async function handleConfirm() {
    if (
      !imageFile ||
      processing
    ) {
      return;
    }

    try {
      setProcessing(true);

      const finalFile =
        await generarImagenFinal();

      onConfirm?.({
        file: finalFile,

        /*
          Los mantenemos disponibles por
          compatibilidad y diagnóstico,
          aunque el archivo ya sale
          físicamente recortado.
        */
        scale,
        x: position.x,
        y: position.y,

        originalSize:
          imageFile.size,

        finalSize:
          finalFile.size
      });

    } catch (error) {
      console.error(
        'Error procesando fotografía:',
        error
      );

      alert(
        error.message ||
        'No fue posible procesar la fotografía'
      );

    } finally {
      setProcessing(false);
    }
  }

  if (!open) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.editor}>

        <header className={styles.header}>
          <button
            type="button"
            className={
              styles.cancelButton
            }
            onClick={onCancel}
            disabled={processing}
          >
            Cancelar
          </button>

          <strong
            className={styles.title}
          >
            Ajustar fotografía
          </strong>

          <button
            type="button"
            className={
              styles.confirmButton
            }
            onClick={
              handleConfirm
            }
            disabled={
              !imageUrl ||
              processing
            }
          >
            {processing
              ? 'Procesando...'
              : 'Listo'}
          </button>
        </header>

        <div
          className={
            styles.instructions
          }
        >
          Mueve y ajusta el vehículo dentro
          del recuadro
        </div>

        <div
          className={
            styles.cropArea
          }
          onPointerDown={
            handlePointerDown
          }
          onPointerMove={
            handlePointerMove
          }
          onPointerUp={
            handlePointerUp
          }
          onPointerCancel={
            handlePointerUp
          }
        >
          {imageUrl && (
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Fotografía del activo"
              className={
                styles.image
              }
              draggable="false"
              style={{
                transform: `
                  translate(
                    calc(-50% + ${position.x}px),
                    calc(-50% + ${position.y}px)
                  )
                  scale(${scale})
                `
              }}
            />
          )}

          <div
            ref={frameRef}
            className={
              styles.frame
            }
            aria-hidden="true"
          />

          <div
            className={
              styles.previewLabel
            }
          >
            Vista del carrusel
          </div>
        </div>

        <div
          className={
            styles.controls
          }
        >
          <div
            className={
              styles.zoomRow
            }
          >
            <span
              className={
                styles.zoomSmall
              }
            >
              −
            </span>

            <input
              type="range"
              min="0.7"
              max="2.5"
              step="0.01"
              value={scale}
              onChange={
                handleZoom
              }
              className={
                styles.zoomSlider
              }
              aria-label="Zoom de fotografía"
              disabled={processing}
            />

            <span
              className={
                styles.zoomLarge
              }
            >
              +
            </span>
          </div>

          <button
            type="button"
            className={
              styles.resetButton
            }
            onClick={
              handleReset
            }
            disabled={processing}
          >
            Restablecer encuadre
          </button>
        </div>

        <p className={styles.help}>
          Usa el dedo para mover la fotografía
          y el control para acercarla o alejarla.
        </p>

      </div>
    </div>
  );
}

export default VehiclePhotoEditor;